/**
 * EndorsementsSection - WBC3 Endorsements Selection Component
 * Extracted from StepVesselIdentity to maintain file size limits
 */

import React from 'react';
import WizardSection from '../../WizardSection';

const EndorsementsSection = ({ currentConfig, onEndorsementChange, onBaseCertificateChange }) => {
  return (
    <>
      {/* Base Certificate Type */}
      <div className="mt-4">
        <WizardSection.Field
          label="Base Certificate Type *"
          help="Your primary WBC3 certificate type - determines core operational framework"
        >
          <div className="space-y-3">
            {[
              { value: 'workboat', label: 'Standard Workboat Certificate', description: 'Most common certificate for commercial workboats' },
              { value: 'light_duty_workboat', label: 'Light Duty Workboat Certificate', description: 'Restricted operations - cargo ≤1000kg, no lifting/towing/dangerous goods', warning: true },
              { value: 'pilot_boat_dedicated', label: 'Dedicated Pilot Boat Certificate', description: 'Specialized certificate for pilot transfer operations only' },
              { value: 'police_boat', label: 'Police Boat Certificate (WBC3 Annex 3)', description: 'Enhanced safety requirements for law enforcement operations' },
              { value: 'non_self_propelled', label: 'Non-Self-Propelled Workboat Certificate', description: 'Unmanned working platforms (barges, pontoons) - simplified platform-specific requirements only' }
            ].map((cert) => (
              <div key={cert.value} className={`relative p-4 border rounded-lg transition-colors ${
                currentConfig.baseCertificate === cert.value
                  ? 'bg-cyan-50 border-cyan-200' 
                  : cert.warning 
                    ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    value={cert.value}
                    checked={currentConfig.baseCertificate === cert.value}
                    onChange={(e) => onBaseCertificateChange(e.target.value)}
                    className="h-4 w-4 mt-1 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{cert.label}</span>
                      {cert.warning && <span className="text-amber-500">⚠️</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </WizardSection.Field>
      </div>

      {/* Endorsements Section */}
      {currentConfig.baseCertificate === 'workboat' && (
        <div className="mt-6">
          <WizardSection.Field
            label="Commercial Endorsements (Optional)"
            help="Select endorsements that authorize additional commercial operations beyond standard workboat activities"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'towing', label: 'Towing Endorsement', description: 'Commercial towing operations (WBC3 26.1.5)', icon: '⚓' },
                { value: 'pilot_boat', label: 'Pilot Boat Endorsement', description: 'Pilot transfer operations (WBC3 Section 27)', icon: '🚁' },
                { value: 'mgo_transfer', label: 'MGO Transfer Endorsement', description: 'Marine Gas Oil transfer operations (WBC3 29.10)', icon: '⛽' },
                { value: 'high_speed', label: 'High-Speed Endorsement', description: 'Operations ≥20 knots or planing mode (WBC3 25.4)', icon: '💨' },
                // NOTE: Tender endorsements removed - tenders operate under parent vessel SMS
              ].map((endorsement) => {
                // Check if this endorsement is disabled for current base certificate
                const isDisabled = currentConfig.baseCertificate === 'light_duty_workboat' && endorsement.value === 'towing';
                
                return (
                  <div key={endorsement.value} className={`relative p-3 border rounded-lg transition-colors ${
                    isDisabled 
                      ? 'bg-gray-100 border-gray-300 opacity-60' 
                      : currentConfig.endorsements.includes(endorsement.value)
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <label className={`flex items-start space-x-3 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={currentConfig.endorsements.includes(endorsement.value)}
                        onChange={(e) => onEndorsementChange(endorsement.value, e.target.checked)}
                        disabled={isDisabled}
                        className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{endorsement.icon}</span>
                          <span className={`text-sm font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                            {endorsement.label}
                            {isDisabled && ' (Not permitted)'}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {isDisabled ? 'WBC3 3.9.3.5 prohibits towing on Light Duty certificates' : endorsement.description}
                        </p>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </WizardSection.Field>
        </div>
      )}
    </>
  );
};

export default EndorsementsSection;